// What's new — release notes in the notification center (Phase 2).
//
// The notes are `docs/RELEASES.md`, shipped INSIDE the build they describe (the
// store imports it `?raw`), so they always match the code that is running — the
// bundled iOS app included. `npm run release` writes every entry in one shape:
//
//   ## 0.7.2.0.0 — 2026-10-04 · minor
//
//   - what changed, in a user's words
//
// The only per-user state is ONE marker (`user_preferences.whats_new_seen`, mig
// 0285): the newest version read. Every newer shown release is unread.

/** Levels that appear in What's new (owner, 2026-10-04: subminor and above). */
const SHOWN_LEVELS = new Set(['main', 'major', 'minor', 'subminor']);

export interface ReleaseEntry {
  version: string;
  /** YYYY-MM-DD, as written by the release script. */
  date: string;
  level: string;
  notes: string[];
}

const HEADING = /^## (\d+(?:\.\d+){4}) — (\d{4}-\d{2}-\d{2}) · ([a-z]+)\s*$/;

/** Every entry of RELEASES.md, in file order (newest first). */
export function parseReleases(markdown: string): ReleaseEntry[] {
  const entries: ReleaseEntry[] = [];
  let current: ReleaseEntry | null = null;
  for (const line of markdown.split(/\r?\n/)) {
    const heading = HEADING.exec(line);
    if (heading) {
      current = { version: heading[1], date: heading[2], level: heading[3], notes: [] };
      entries.push(current);
      continue;
    }
    if (line.startsWith('## ')) {
      current = null; // a heading that is not a release — stop collecting
      continue;
    }
    const bullet = /^- (.+)$/.exec(line);
    if (current && bullet) current.notes.push(bullet[1].trim());
  }
  return entries;
}

/** Releases that What's new shows: subminor and above, with something to say. */
export function shownReleases(entries: ReleaseEntry[]): ReleaseEntry[] {
  return entries.filter((e) => SHOWN_LEVELS.has(e.level) && e.notes.length > 0);
}

/** Numeric comparison of five-level versions; <0, 0, >0 like a sort comparator. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export function isUnread(entry: ReleaseEntry, seen: string | null): boolean {
  return seen === null || compareVersions(entry.version, seen) > 0;
}

/**
 * The marker for someone who has none yet (owner, 2026-10-04): an existing account
 * gets ONLY THE NEWEST release unread; an account created on or after the newest
 * release's day gets nothing unread — it was never "new" to them.
 *
 * `shown` is newest first. Returns null when there is nothing to show.
 */
export function baselineSeen(shown: ReleaseEntry[], accountCreatedAt: string | null): string | null {
  const newest = shown[0];
  if (!newest) return null;
  const createdDay = accountCreatedAt ? accountCreatedAt.slice(0, 10) : null;
  if (createdDay && createdDay >= newest.date) return newest.version;
  // everything below the newest counts as read; with no older release, "0" marks
  // nothing read so the newest is the one unread item
  return shown[1]?.version ?? '0.0.0.0.0';
}

/** The newest version among `shown` (for "Mark all read"). */
export function newestVersion(shown: ReleaseEntry[]): string | null {
  return shown[0]?.version ?? null;
}

/** A release has a day, not a time: it sorts at the start of that day (UTC). */
export function releaseCreatedAt(entry: ReleaseEntry): string {
  return `${entry.date}T00:00:00.000Z`;
}
